import { SearchContext } from "src/common/search";

import React, { Dispatch, SetStateAction, useContext } from "react";
import { FaExpand } from "react-icons/fa";
import ToggleSwitch from "src/components/dashboard/ToggleSwitch";
import {
  DASHBOARD_CHART_CARD_HEIGHT_CLASS,
  DASHBOARD_COMPACT_CHART_CARD_HEIGHT_CLASS,
} from "src/helpers/chart-helpers";

interface ChartCardProps {
  title: string | React.ReactNode;
  children: React.ReactNode;
  headerAccessory?: React.ReactNode;
  onExpand?: () => void;
  ontoggle?: boolean;
  setToggleChart?: Dispatch<SetStateAction<boolean>>;
  toggleChart?: boolean;
  isDashboardChart?: boolean;
  isHPB?: boolean;
  isPatternInsight?: boolean;
  isPatternInsightModal?: boolean;
  isOrderblock?: boolean;
  /** Shorter title + padding for dashboard triple-row cards */
  compactHeader?: boolean;
  /** Allow long titles to wrap instead of truncating */
  wrapTitle?: boolean;
  className?: string;
  /** Override default chart body height (e.g. compact modal layouts) */
  bodyHeightClass?: string;
  /** Do not display the selected company symbol prefix in the header */
  hideSymbol?: boolean;
  showSymbol?: boolean;
}

const ChartCard: React.FC<ChartCardProps> = ({
  title,
  children,
  headerAccessory,
  onExpand,
  ontoggle,
  setToggleChart,
  toggleChart,
  isDashboardChart,
  isPatternInsight,
  isPatternInsightModal,
  isOrderblock,
  compactHeader,
  wrapTitle,
  className = "",
  bodyHeightClass,
  hideSymbol,
  showSymbol = true,
}) => {
  const searchContext = useContext(SearchContext);
  const { selectedCompany, isLoading } = searchContext;

  const isFixedHeightDashboard = isDashboardChart && !compactHeader;
  const isCompactDashboard = isDashboardChart && compactHeader;
  const shouldShowSymbol = !hideSymbol && showSymbol;

  const renderTitle = (truncate = false) => (
    <h3
      className={`min-w-0 font-semibold leading-tight ${compactHeader ? "text-[11px] xl:text-xs" : "text-sm"} ${truncate ? "truncate" : "shrink-0"}`}
    >
      {isDashboardChart &&
      shouldShowSymbol &&
      selectedCompany.symbol &&
      !isLoading ? (
        wrapTitle ? (
          <span className="block min-w-0 truncate">
            {selectedCompany.symbol}: {title}
          </span>
        ) : (
          <span
            className={`flex min-w-0 items-baseline gap-1 ${truncate ? "truncate" : ""}`}
          >
            <span className="shrink-0">{selectedCompany.symbol}:</span>
            <span className={`min-w-0 ${truncate ? "truncate" : ""}`}>
              {title}
            </span>
          </span>
        )
      ) : (
        title
      )}
    </h3>
  );

  const renderControls = () => (
    <div className="flex shrink-0 items-center gap-3 text-md">
      {ontoggle && (
        <>
          {isPatternInsight ? (
            <div className="flex items-center gap-2 text-sm font-medium">
              <button
                onClick={() => setToggleChart && setToggleChart(false)}
                className={`transition-colors cursor-pointer ${
                  !toggleChart
                    ? "text-green-600 font-semibold"
                    : "dark:text-white text-gray-600 hover:text-gray-400"
                }`}
              >
                Market
              </button>

              <span className="text-gray-400">|</span>
              <button
                onClick={() => setToggleChart && setToggleChart(true)}
                className={`transition-colors cursor-pointer ${
                  toggleChart
                    ? "text-green-600 font-semibold"
                    : "text-gray-600 dark:text-white hover:text-gray-400"
                }`}
              >
                SEBI
              </button>
            </div>
          ) : (
            <div
              role="button"
              onClick={() => setToggleChart && setToggleChart(!toggleChart)}
              className="flex scale-80"
            >
              <ToggleSwitch
                checked={toggleChart || false}
                onChange={() => setToggleChart && setToggleChart(!toggleChart)}
              />
            </div>
          )}
        </>
      )}
      {onExpand && (
        <button
          onClick={onExpand}
          className="cursor-pointer text-gray-500 transition-colors hover:text-gray-800 dark:hover:text-white"
          title="View full size"
        >
          <FaExpand size={18} />
        </button>
      )}
    </div>
  );

  return (
    <div
      className={`${!isPatternInsightModal ? "dark:bg-[#121419] bg-white" : ""} min-w-0 backdrop-blur-lg rounded-lg border border-gray-200 p-1 shadow-sm dark:border-gray-700 dark:shadow-2xl ${isFixedHeightDashboard ? `flex flex-col ${DASHBOARD_CHART_CARD_HEIGHT_CLASS}` : isCompactDashboard ? `flex flex-col ${DASHBOARD_COMPACT_CHART_CARD_HEIGHT_CLASS}` : isOrderblock ? "flex flex-col h-full" : ""} ${className}`}
    >
      <div
        className={`mb-1 shrink-0 ${compactHeader ? "h-[26px] overflow-hidden px-0.5" : ""} ${headerAccessory ? "space-y-0.5" : ""}`}
      >
        {headerAccessory ? (
          <>
            <div className="flex items-center justify-between gap-2">
              <div className="flex min-w-0 flex-1 items-center gap-2 min-[1709px]:items-start">
                {renderTitle(true)}
                <div className="hidden min-w-0 flex-1 pt-0.5 min-[1709px]:block">
                  {headerAccessory}
                </div>
              </div>
              {renderControls()}
            </div>
            <div className="ml-2 mr-4 h-[18px] min-h-[18px] overflow-hidden min-[1709px]:hidden">
              {headerAccessory}
            </div>
          </>
        ) : (
          <div
            className={`flex items-center justify-between gap-1 ${compactHeader ? "" : "gap-2"}`}
          >
            <div className="min-w-0 flex-1">{renderTitle()}</div>
            {renderControls()}
          </div>
        )}
      </div>
      <div
        className={`flex min-h-0 flex-1 flex-col overflow-hidden ${
          bodyHeightClass
            ? bodyHeightClass
            : isFixedHeightDashboard || isOrderblock
              ? ""
              : isDashboardChart && !compactHeader
                ? "h-85"
                : !isDashboardChart
                  ? "h-[30vh]"
                  : ""
        }`}
      >
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </div>
  );
};

export default ChartCard;
